import React, { useState, useEffect, useRef } from 'react'
import { useGameplayContext } from '../contexts/gameplayContext'
import {
  STOKE_BUBBLE_SIZES,
  STOKE_BUBBLE_INTERVAL_MS,
  STOKE_COMBO_DECAY_MS,
  MAX_STOKE_COMBO_MULTIPLIER
} from '@/constants/stoke'
import { formatCurrency } from '@/utils'
import { X } from 'lucide-react'

// Add a style block with keyframes for rotation and movement
const animationStyles = `
  @keyframes spin-cw {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes spin-ccw {
    from { transform: rotate(0deg); }
    to { transform: rotate(-360deg); }
  }
  @keyframes pop {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.3); opacity: 0.7; }
    100% { transform: scale(0.1); opacity: 0; }
  }
  @keyframes float-vertical {
    0% { top: var(--initial-top); }
    50% { top: calc(var(--initial-top) - 30px); }
    100% { top: var(--initial-top); }
  }
  @keyframes float-horizontal {
    0% { left: var(--initial-left); }
    50% { left: calc(var(--initial-left) + 30px); }
    100% { left: var(--initial-left); }
  }
  @keyframes float-diagonal {
    0% { 
      top: var(--initial-top);
      left: var(--initial-left);
    }
    50% { 
      top: calc(var(--initial-top) - 20px);
      left: calc(var(--initial-left) + 20px);
    }
    100% { 
      top: var(--initial-top);
      left: var(--initial-left);
    }
  }
`

interface StokeBubble {
  id: number
  size: 'SMALL' | 'MEDIUM' | 'LARGE'
  x: number
  y: number
  amount: number
  displayTimeMs: number
  startTime: number
  rotationSpeed: string
  rotationDirection: 'cw' | 'ccw'
  moveDirection: 'horizontal' | 'vertical' | 'diagonal'
  isPopping?: boolean
}

export const StokeCatchingGame: React.FC = () => {
  const { earnStoke, gameData, setGameData } = useGameplayContext()
  const [bubbles, setBubbles] = useState<StokeBubble[]>([])
  const [combo, setCombo] = useState(0)
  const [highestCombo, setHighestCombo] = useState(1)
  const [comboMultiplier, setComboMultiplier] = useState(1)
  const [lastCatchTime, setLastCatchTime] = useState(0)
  const [totalCaught, setTotalCaught] = useState(0)
  const gameAreaRef = useRef<HTMLDivElement>(null)
  const bubbleIdRef = useRef(0)
  const comboTimeoutRef = useRef<NodeJS.Timeout>()
  const bubbleIntervalRef = useRef<NodeJS.Timeout>()
  const animationFrameRef = useRef<number>()

  // Check if stoke is at maximum capacity
  const isStokeMaxed = gameData.currencies.stoke.amount >= (gameData.currencies.stoke.max || Infinity)

  // Check if user is currently surfing a green wave
  const isGreenWaveSurfing = gameData.isSurfingGreenWave

  // Check if stoke catching is enabled
  const isStokeEnabled = gameData.catchStoke

  // Start game automatically when mounted and generate first bubble
  useEffect(() => {
    // Always start the game if user is surfing a green wave, regardless of stoke level
    if (isGreenWaveSurfing && isStokeEnabled) {
      startGame()
    }

    // Clean up when unmounting or no longer surfing green wave
    return () => stopGame()
  }, [isGreenWaveSurfing, isStokeEnabled])

  // Update critProbabilityComboMultiplier when highest combo changes
  useEffect(() => {
    if (highestCombo > 1) {
      setGameData((prevData) => ({
        ...prevData,
        surfSession: {
          ...prevData.surfSession,
          critProbabilityComboMultiplier: 0.5 + highestCombo * 0.5
        }
      }))
    }
  }, [highestCombo, setGameData])

  // Start the game
  const startGame = () => {
    setBubbles([])
    setCombo(0)
    setComboMultiplier(1)
    setTotalCaught(0)
    bubbleIdRef.current = 0

    // Immediately generate first bubble
    generateBubble()

    // Start interval for future bubbles
    bubbleIntervalRef.current = setInterval(generateBubble, STOKE_BUBBLE_INTERVAL_MS)

    // Start animation frame for checking expired bubbles
    const updateFrame = () => {
      checkExpiredBubbles()
      animationFrameRef.current = requestAnimationFrame(updateFrame)
    }
    animationFrameRef.current = requestAnimationFrame(updateFrame)
  }

  // Stop the game
  const stopGame = () => {
    setBubbles([])
    clearTimeout(comboTimeoutRef.current)
    clearInterval(bubbleIntervalRef.current)
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }

  // Handle exit button click
  const handleExit = () => {
    setGameData((prevData) => ({
      ...prevData,
      catchStoke: false
    }))
    stopGame()
  }

  // Generate a random bubble
  const generateBubble = () => {
    if (!gameAreaRef.current || !isGreenWaveSurfing || !isStokeEnabled || (isStokeMaxed && bubbles.length > 0)) return

    const gameArea = gameAreaRef.current
    const rect = gameArea.getBoundingClientRect()

    // Determine bubble size based on probabilities
    let sizes = Object.keys(STOKE_BUBBLE_SIZES) as Array<'SMALL' | 'MEDIUM' | 'LARGE'>
    let probabilities = sizes.map((size) => STOKE_BUBBLE_SIZES[size].probability)

    // Weighted random selection
    let random = Math.random()
    let cumulativeProbability = 0
    let selectedSize: 'SMALL' | 'MEDIUM' | 'LARGE' = 'SMALL'

    for (let i = 0; i < sizes.length; i++) {
      cumulativeProbability += probabilities[i]
      if (random <= cumulativeProbability) {
        selectedSize = sizes[i]
        break
      }
    }

    const bubbleProps = STOKE_BUBBLE_SIZES[selectedSize]

    // Determine random movement direction
    const directions: ('horizontal' | 'vertical' | 'diagonal')[] = ['horizontal', 'vertical', 'diagonal']
    const moveDirection = directions[Math.floor(Math.random() * directions.length)]

    // Calculate position - keep bubbles within game area
    // Allow more space for movement
    const padding = 100
    const maxX = rect.width - padding
    const maxY = rect.height - padding

    const newBubble: StokeBubble = {
      id: bubbleIdRef.current++,
      size: selectedSize,
      x: Math.max(padding / 2, Math.floor(Math.random() * maxX)),
      y: Math.max(padding / 2, Math.floor(Math.random() * maxY)),
      amount: bubbleProps.amount,
      displayTimeMs: bubbleProps.displayTimeMs,
      startTime: Date.now(),
      rotationSpeed: `${Math.random() * 3 + 2}s`, // Random between 2-5s
      rotationDirection: Math.random() > 0.5 ? 'cw' : 'ccw', // Random direction
      moveDirection: moveDirection,
      isPopping: false
    }

    setBubbles((prev) => [...prev, newBubble])
  }

  // Handle bubble click
  const catchBubble = (bubble: StokeBubble) => {
    const bubbleProps = STOKE_BUBBLE_SIZES[bubble.size]
    const now = Date.now()

    // Update combo
    clearTimeout(comboTimeoutRef.current)

    const timeSinceLastCatch = now - lastCatchTime
    if (timeSinceLastCatch < STOKE_COMBO_DECAY_MS) {
      setCombo((prev) => {
        const newCombo = prev + 1
        // Update highest combo if current combo is higher
        if (newCombo > highestCombo) {
          setHighestCombo(newCombo)
        }
        return newCombo
      })

      // Calculate combo multiplier
      const newMultiplier = Math.min(bubbleProps.comboMultiplier * (1 + combo * 0.1), MAX_STOKE_COMBO_MULTIPLIER)
      setComboMultiplier(newMultiplier)
    } else {
      // Reset combo if it's been too long
      setCombo(1)
      setComboMultiplier(bubbleProps.comboMultiplier)
    }

    setLastCatchTime(now)

    // Set timeout to reset combo
    comboTimeoutRef.current = setTimeout(() => {
      setCombo(0)
      setComboMultiplier(1)
    }, STOKE_COMBO_DECAY_MS)

    // Calculate stoke earned with combo multiplier
    const stokeEarned = bubble.amount * comboMultiplier

    // Only actually earn stoke if not maxed
    if (!isStokeMaxed) {
      earnStoke(stokeEarned)
    }

    setTotalCaught((prev) => prev + stokeEarned)

    // Start pop animation and remove bubble after animation completes
    setBubbles((prev) => prev.map((b) => (b.id === bubble.id ? { ...b, isPopping: true } : b)))

    // Remove bubble after animation completes
    setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.id !== bubble.id))
    }, 300) // 300ms is the duration of the pop animation
  }

  // Check for expired bubbles
  const checkExpiredBubbles = () => {
    const now = Date.now()
    setBubbles((prev) =>
      prev.filter((bubble) => {
        return now - bubble.startTime < bubble.displayTimeMs
      })
    )
  }

  const getBubbleSize = (size: 'SMALL' | 'MEDIUM' | 'LARGE') => {
    switch (size) {
      case 'SMALL':
        return 'w-12 h-12'
      case 'MEDIUM':
        return 'w-16 h-16'
      case 'LARGE':
        return 'w-20 h-20'
    }
  }

  // Get movement duration based on bubble size
  const getBubbleMovementDuration = (size: 'SMALL' | 'MEDIUM' | 'LARGE') => {
    switch (size) {
      case 'SMALL':
        return '0.9s' // Faster for small bubbles
      case 'MEDIUM':
        return '1.3s' // Medium speed
      case 'LARGE':
        return '2s' // Slower for large bubbles
    }
  }

  // If not surfing green wave or stoke catching is disabled, don't render anything
  if (!isGreenWaveSurfing || !isStokeEnabled) {
    return null
  }

  // Get crit probability display
  const critProb =
    gameData.currencies.crit_probability.amount *
    (gameData.currencies.crit_probability.totalMultiplier || 1) *
    gameData.surfSession.critProbabilityComboMultiplier

  const critProbDisplay = `${(critProb * 100).toFixed(1)}%`

  return (
    <>
      {/* Add the keyframes style */}
      <style>{animationStyles}</style>

      {/* Dark overlay container with high z-index */}
      <div className="fixed inset-0 bg-black bg-opacity-50 pointer-events-auto" style={{ zIndex: 900 }}>
        {/* Game area for bubbles */}
        <div ref={gameAreaRef} className="w-full h-full relative">
          {bubbles.map((bubble) => {
            const moveDuration = getBubbleMovementDuration(bubble.size)

            return (
              <button
                key={bubble.id}
                onClick={() => !bubble.isPopping && catchBubble(bubble)}
                className={`absolute rounded-full ${getBubbleSize(
                  bubble.size
                )} flex items-center justify-center bg-no-repeat bg-center bg-cover transform hover:scale-105 transition-transform cursor-pointer shadow-lg hover:shadow-xl pointer-events-auto ${
                  bubble.isPopping ? '' : 'animate-pulse'
                }`}
                style={
                  {
                    left: `${bubble.x}px`,
                    top: `${bubble.y}px`,
                    backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="50%" x="50%" dominant-baseline="middle" text-anchor="middle" font-size="80">🤙</text></svg>')`,
                    animation: bubble.isPopping
                      ? 'pop 0.3s ease-out forwards'
                      : `float-${bubble.moveDirection} ${moveDuration} ease-in-out infinite, spin-${bubble.rotationDirection} ${bubble.rotationSpeed} linear infinite`,
                    transformOrigin: 'center center',
                    pointerEvents: bubble.isPopping ? 'none' : 'auto',
                    '--initial-left': `${bubble.x}px`,
                    '--initial-top': `${bubble.y}px`,
                    zIndex: 9999
                  } as React.CSSProperties
                }
              >
                <div
                  className={`text-orange-900 font-bold text-sm md:text-lg ${bubble.isPopping ? 'animate-bounce' : ''}`}
                >
                  +{bubble.amount}
                </div>
              </button>
            )
          })}
        </div>

        {/* Floating stats window at top right with exit button - positioned with a fixed offset from the right edge */}
        <div className="fixed top-16 right-0 mr-8 pointer-events-auto" style={{ zIndex: 9990 }}>
          <div className="bg-blue-900/90 rounded-lg shadow-lg p-2 flex flex-col min-w-[200px] relative">
            {/* Exit button */}
            <button
              onClick={handleExit}
              className="absolute -top-3 -right-3 bg-red-500 hover:bg-red-600 rounded-full p-1 text-white shadow-md transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Combo counter on top of both "cells" */}
            <div className="text-center mb-1">
              <div className={`text-yellow-400 font-bold text-xl ${combo > 1 ? 'animate-pulse text-yellow-300' : ''}`}>
                Combo: {combo > 0 ? `x${combo}` : 'x1'}
                {highestCombo > 1 && combo < highestCombo && (
                  <span className="text-sm ml-1">(Best: x{highestCombo})</span>
                )}
              </div>
            </div>

            {/* Two vertical sections */}
            <div className="flex flex-row justify-between gap-4">
              {/* Left section - Stoke */}
              <div className="flex-1 bg-blue-950/50 rounded-md p-2 flex flex-col items-center justify-center">
                <div className="text-yellow-400 font-bold mb-1">🤙 Stoke</div>
                <div className="font-semibold text-yellow-300">
                  {isStokeMaxed ? 'FULL' : formatCurrency(totalCaught)}
                </div>
                {!isStokeMaxed && (
                  <div className="text-xs text-yellow-400 mt-1">
                    Mult: {gameData.currencies.stoke.totalMultiplier?.toFixed(1)}x
                  </div>
                )}
              </div>

              {/* Right section - Crit % */}
              <div className="flex-1 bg-blue-950/50 rounded-md p-2 flex flex-col items-center justify-center">
                <div className="text-yellow-400 font-bold mb-1">⚡ Crit %</div>
                <div className="font-semibold text-yellow-300">{critProbDisplay}</div>
                <div className="text-xs text-yellow-400 mt-1">
                  Boost: {gameData.surfSession.critProbabilityComboMultiplier.toFixed(1)}x
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
